import { useTranslation } from 'react-i18next';
import truncate from 'lodash/truncate';
import { Badge, Flex, Group, Stack, Text } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';

import IconInProgress from '@ui-kit/atoms/LoaderIcon';
import renderPermissionIcon from '@features/Permission/utils/renderPermissionIcon';
import { palette } from '@app/shared/ui-kit/colors';
import { GroupPermission } from '../hooks/useParentPermissions';

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const GROUP_NAME_MAX_LENGTH = 12;
const BADGE_MIN_WIDTH = 60;

/* -------------------------------------------------------------------------- */
/*                              Sub-components                                */
/* -------------------------------------------------------------------------- */

export function PermissionBadgesLoading() {
  const { t } = useTranslation(['Common']);

  return (
    <Flex align="center" gap={8}>
      <IconInProgress loading size={14} />
      <Text size="xs" c="dimmed">
        {t('Common:cta.loading')}
      </Text>
    </Flex>
  );
}

export function PermissionBadgesError() {
  const { t } = useTranslation(['Resource']);

  return (
    <Group gap={4}>
      <IconLock size={14} color={palette.blackroomGray} />
      <Text size="xs" c="dimmed">
        {t('Resource:DialogUploadPermissions.permissionsError')}
      </Text>
    </Group>
  );
}

export function PermissionBadgesMultipleDestinations() {
  const { t } = useTranslation(['Resource']);

  return (
    <Group gap={4}>
      <IconLock size={14} color={palette.blackroomGray} />
      <Text size="xs" c="dimmed">
        {t('Resource:DialogUploadPermissions.multipleDestinations')}
      </Text>
    </Group>
  );
}

export function PermissionBadgesEmpty() {
  const { t } = useTranslation(['Resource']);

  return (
    <Group gap={4}>
      <IconLock size={14} color={palette.blackroomGray} />
      <Text size="xs" c="dimmed">
        {t('Resource:DialogUploadPermissions.noPermissions')}
      </Text>
    </Group>
  );
}

interface PermissionBadgesListProps {
  groupPermissions: GroupPermission[];
}

export function PermissionBadgesList({
  groupPermissions,
}: PermissionBadgesListProps) {
  const { t } = useTranslation(['Permission', 'Resource']);

  return (
    <Stack gap="xs">
      {groupPermissions.map((groupPerm) => (
        <Group key={groupPerm.groupId} gap="xs" wrap="nowrap">
          <Badge
            color={groupPerm.groupColor}
            variant="filled"
            size="xs"
            style={{ minWidth: BADGE_MIN_WIDTH }}
          >
            {truncate(groupPerm.groupName, { length: GROUP_NAME_MAX_LENGTH })}
          </Badge>
          <Group gap={4}>
            {groupPerm.permissions.map((permission) => (
              <Group key={permission} gap={2}>
                {renderPermissionIcon(permission, { size: 12 })}
                <Text size="xs">{t(`Permission:Common.${permission}`)}</Text>
              </Group>
            ))}
          </Group>
        </Group>
      ))}
      <Text size="xs" c="dimmed" fs="italic">
        {t('Resource:DialogUploadPermissions.otherGroupsNoAccess')}
      </Text>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Main Component                               */
/* -------------------------------------------------------------------------- */

interface PermissionBadgesProps {
  isLoading: boolean;
  hasError: boolean;
  hasMultipleDestinations: boolean;
  isAtRoot: boolean;
  hasPermissions: boolean;
  groupPermissions: GroupPermission[];
}

export default function PermissionBadges({
  isLoading,
  hasError,
  hasMultipleDestinations,
  isAtRoot,
  hasPermissions,
  groupPermissions,
}: PermissionBadgesProps) {
  if (isLoading) {
    return <PermissionBadgesLoading />;
  }

  if (hasError) {
    return <PermissionBadgesError />;
  }

  if (hasMultipleDestinations) {
    return <PermissionBadgesMultipleDestinations />;
  }

  if (isAtRoot || !hasPermissions) {
    return <PermissionBadgesEmpty />;
  }

  return <PermissionBadgesList groupPermissions={groupPermissions} />;
}
